import { ArrowsUpDownIcon, ChevronDownIcon, ChevronLeftIcon, ChevronRightIcon, ChevronUpIcon, FunnelIcon, XMarkIcon } from '@heroicons/react/24/outline';
import React, { useState, useMemo, useEffect } from 'react';
import { Button } from './Button';
import { ButtonVariant } from '../../utils/utils';
import { Option } from './Select';
import uiConfig from '../../data/uiConfig';
import { Card } from './Card';
import { CardActions } from './CardActions';
import { Dropdown } from './Dropdown';
import clsx from 'clsx';

interface Column<T> {
  key: keyof T;
  label: string;
  sortable?: boolean;
  render?: (value: any, row: T) => React.ReactNode;
  filterOptions?: Option[];
  hidden?: boolean;
}

interface Action<T> {
  label: string;
  icon?: React.ReactElement;
  buttonVariant?: ButtonVariant;
  onClick: (row: T) => void;
  hidden?: boolean | ((row: T) => boolean);
}

interface DataTableProps<T> {
  columns: Column<T>[];
  data: T[];
  actions?: Action<T>[];
  pagination?: boolean;
  pageSize?: number;
  actionWidth?: string;
  headerBg?: string;
  headerBorder?: string;
  columnBg?: string;
  headerIsDark?: boolean;
}

export function DataTable<T>({
  columns,
  data,
  actions = [],
  pagination = uiConfig.datatable.pagination,
  pageSize = uiConfig.datatable.pageSize,
  actionWidth = 'w-1/4',
  headerBg = uiConfig.datatable.headerBg,
  columnBg = uiConfig.datatable.columnBg,
  headerIsDark = uiConfig.datatable.headerIsDark
}: DataTableProps<T>) {
  headerIsDark = localStorage.getItem('theme') === 'dark' ? true : headerIsDark
  const classIconHeader = `w-4 h-4 ${headerIsDark ? "text-white hover:text-gray-300" : "text-gray-500 hover:text-gray-800"}`

// Détecter si on est sur mobile/tablet
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
  const checkIsMobile = () => {
    setIsMobile(window.innerWidth < 1024); // lg breakpoint de Tailwind
  };

  checkIsMobile();
  window.addEventListener('resize', checkIsMobile);

  return () => window.removeEventListener('resize', checkIsMobile);
}, []);


  pageSize = isMobile ? pageSize/2 : pageSize;

  //Gestion des filtres
  const [activeFilters, setActiveFilters] = useState<Record<string, string>>({});
  const [openFilter, setOpenFilter] = useState<string | null>(null);

  // filteredData applique les filtres actifs sur les données :
  // - Pour chaque filtre actif, on ne garde que les lignes où la valeur correspond exactement
  const filteredData = useMemo(() => {
    return data.filter((item) => {
      return Object.entries(activeFilters).every(([key, value]) => {
        if (!value) return true; // Si pas de filtre sur cette colonne, on garde la ligne
        const itemValue = String(item[key as keyof T]);
        return itemValue === value;
      });
    });
  }, [data, activeFilters]);

  //Gestion du tri
  const [sortConfig, setSortConfig] = useState<{ key: keyof T; direction: 'asc' | 'desc' } | null>(null);

  // sortedData trie les données filtrées selon de tri (sortConfig) :
  // useMemo permet de ne recalculer le tri que si filteredData ou sortConfig changent.
  const sortedData = useMemo(() => {
    if (!sortConfig) return filteredData; // Pas de tri actif
    return [...filteredData].sort((a, b) => {
      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];

      // Cas où les valeurs sont identiques
      if (aValue === bValue) return 0;

      // Cas où une valeur est null/undefined
      if (aValue == null) return 1;
      if (bValue == null) return -1;

      // Tri numérique si les deux valeurs sont des nombres
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
      }

      // Tri alphabétique sinon
      return sortConfig.direction === 'asc'
        ? String(aValue).localeCompare(String(bValue))
        : String(bValue).localeCompare(String(aValue));
    });
  }, [filteredData, sortConfig]);

  //Gestion de la pagination
  const [currentPage, setCurrentPage] = useState(1);
  const totalPages = Math.ceil(filteredData.length / pageSize);

  const enablePagination = pagination || pageSize < filteredData.length;


  const paginatedData = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return sortedData.slice(start, start + pageSize);
  }, [sortedData, currentPage, pageSize]);

  // Remettre la pagination à la première page quand les filtres changent
  React.useEffect(() => {
    setCurrentPage(1);
  }, [activeFilters])

  // Pour le mobile 
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);



  return (
    <>
      <div className="hidden md:block">
        {/* Table */}
        <table className="min-w-full border text-sm">
          <thead>
            <tr className={`${headerBg}`}>
              {/* Header avec un tri et un filtre si l'option est selectionné */}
              {columns.map((col) => (
                <th key={String(col.key)} className={clsx("relative p-2 border-b cursor-pointer select-none", col.hidden && "hidden")}>

                  <span className="flex items-center gap-1">
                    {!col.sortable && col.label}
                    {/* Tri */}
                    {col.sortable && (
                      <span
                        className="cursor-pointer"
                        onClick={() => {
                          if (!col.sortable) return;
                          setSortConfig((prev) => {
                            if (!prev || prev.key !== col.key) {
                              return { key: col.key, direction: 'desc' };
                            }
                            if (prev.direction === 'desc') {
                              return { key: col.key, direction: 'asc' };
                            }
                            return null;
                          });
                        }}
                      >
                        <span className='mr-2'>{col.label}</span>
                        {sortConfig?.key === col.key
                          ? sortConfig.direction === 'desc'
                            ? <ChevronDownIcon className={`inline ${classIconHeader}`} aria-label='Tri décroissant' />
                            : sortConfig.direction === 'asc'
                              ? <ChevronUpIcon className={`inline ${classIconHeader}`} aria-label='Tri croissant' />
                              : <ArrowsUpDownIcon className={`inline ${classIconHeader}`} aria-label='Annuler le tri' />
                          : <ArrowsUpDownIcon className={`inline ${classIconHeader}`} aria-label='Trier la colonne' />}
                      </span>
                    )}
                    {/* Filtre */}
                    {col.filterOptions && (
                      <button
                        className="ml-auto"
                        onClick={(e) => {
                          e.stopPropagation();
                          setOpenFilter(openFilter === String(col.key) ? null : String(col.key));
                        }}
                        aria-label={`Filtrer ${col.label}`}
                      >
                        <FunnelIcon className={classIconHeader} />
                      </button>
                    )}
                  </span>
                  {/* Dropdown filtre */}
                  {openFilter === String(col.key) && col.filterOptions && (
                    <div className={clsx(
                      "absolute z-10 right-0 mt-2 border rounded shadow p-2 min-w-[120px] font-normal",
                      headerBg
                    )}>
                      <button
                        className={clsx(
                          "block w-full text-left px-2 py-1",
                          headerIsDark ? "hover:text-gray-300" : "hover:text-gray-600"
                        )}
                        onClick={() => {
                          setActiveFilters((prev) => ({ ...prev, [col.key]: "" }));
                          setOpenFilter(null);
                        }}
                      >
                        Tous
                      </button>
                      {col.filterOptions.map((opt) => (
                        <button
                          key={opt.value}
                          className={clsx("block w-full text-left px-2 py-1",
                            headerIsDark ? "hover:text-gray-300" : "hover:text-gray-600",
                            activeFilters?.status === opt.value && "font-bold"
                          )}
                          onClick={() => {
                            setActiveFilters((prev) => ({ ...prev, [col.key]: opt.value }));
                            setOpenFilter(null);
                          }}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  )}




                </th>
              ))}
              {actions.length > 0 && <th className={`p-2 border-b ${actionWidth}`}>Actions</th>}
            </tr>
          </thead>
          <tbody>

            {/* Boucle sur les colonnes */}
            {paginatedData.map((row, i) => (
              <tr key={i} className={`${columnBg + " odd:brightness-90  dark:odd:brightness-125"}`}>
                {columns.map((col) => (
                  <td key={String(col.key)} className={clsx("p-2 border-b", col.hidden && "hidden")}>
                    {col.render ? col.render(row[col.key], row) : String(row[col.key])}
                  </td>
                ))}

                {/* Affiche les boutons d'actions */}
                {actions.length > 0 && (
                  <td className="p-2 border-b space-x-2">
                    <div className='flex space-x-2 justify-end'>
                      {actions.map((action, j) => {
                        const isHidden = typeof action.hidden === 'function' ? action.hidden(row) : !!action.hidden;
                        return !isHidden && (
                          <Button
                            key={j}
                            variant={action.buttonVariant}
                            icon={action.icon}
                            onClick={() => action.onClick(row)}
                          >
                            {action.label}
                          </Button>
                        );
                      })}
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>

      </div>


      {/* Version card pour mobile */}
      <div className='md:hidden space-y-2'>
        <div className='flex space-x-2'>
          {/* Gestion des tri sur mobile nomColonne + ordre de tri */}
          {columns.some(c => c.sortable) && (
            <Card className='sm:min-w-[200px]'>
              <div className='text-xl pb-6'>
                Tri <ArrowsUpDownIcon className='w-6 h-6 inline ml-2' />
              </div>
              {columns.filter(c => c.sortable).map(col => (
                <div className='relative mb-2' key={String(col.key)}>
                  <Button
                    variant={uiConfig.datatable.responsive.filterBtn as ButtonVariant}
                    className={uiConfig.datatable.responsive.filterBtn}
                    onClick={() => setOpenDropdown(openDropdown === `sort_${col.label}` ? null : `sort_${col.label}`)}
                  >
                    {col.label}
                  </Button> 
                  <Dropdown
                    key="Tri"
                    bgColor={uiConfig.datatable.responsive.bgDropdown}
                    arrow
                    isOpen={openDropdown === `sort_${col.label}`}
                    onMouseLeave={() => setOpenDropdown(null)}
                  >
                    <div className="px-4 py-2">
                      <div
                        className={clsx("py-2 flex justify-between cursor-pointer",
                          sortConfig?.key === col.key && sortConfig.direction === 'asc' && 'font-bold'
                        )}
                        onClick={() => setSortConfig({ key: col.key, direction: 'asc' })}
                      >
                        Tri croissant <ChevronUpIcon className='w-4 h-4 inline ml-2' />
                      </div>
                      <div
                        className={clsx("py-2 flex justify-between cursor-pointer",
                          sortConfig?.key === col.key && sortConfig.direction === 'desc' && 'font-bold'
                        )}
                        onClick={() => setSortConfig({ key: col.key, direction: 'desc' })}
                      >
                        Tri décroissant <ChevronDownIcon className='w-4 h-4 inline ml-2' />
                      </div>
                      <div
                        className="py-2 flex justify-between cursor-pointer"
                        onClick={() => setSortConfig(null)}
                      >
                        Annuler le tri <XMarkIcon className='w-4 h-4 inline ml-2' />
                      </div>
                    </div>
                  </Dropdown>
                </div>
              ))}
            </Card>
          )}

          {/* Gestion des filtres sur mobile nomColonne + ordre de tri */}
          {columns.some(c => c.filterOptions) && (
            <Card className='sm:min-w-[200px]'>
              <div className='text-xl pb-6'>
                Filtre <FunnelIcon className='w-6 h-6 inline ml-2' />
              </div>
              {columns.filter(c => c.filterOptions).map(col => (
                <div className='relative mb-2' key={String(col.key)}>
                  <Button
                    variant={uiConfig.datatable.responsive.filterBtn as ButtonVariant}
                    onClick={() => setOpenDropdown(openDropdown === `filter_${col.label}` ? null : `filter_${col.label}`)}
                  >
                    {col.label}
                  </Button>
                  <Dropdown
                    key="filtre"
                    bgColor={uiConfig.datatable.responsive.bgDropdown}
                    arrow
                    isOpen={openDropdown === `filter_${col.label}`}
                    onMouseLeave={() => setOpenDropdown(null)}
                  >
                    <div className="px-4 py-2">
                      {col.filterOptions && (
                        <>
                          <div
                            className="py-2 flex justify-between cursor-pointer"
                            onClick={() => {
                              setActiveFilters((prev) => ({ ...prev, [col.key]: "" }));
                              setOpenFilter(null);
                            }}
                          >
                            Tous
                          </div>
                          {col.filterOptions.map((opt) => (
                            <div
                              className={clsx("py-2 flex justify-between cursor-pointer",
                                activeFilters?.status === opt.value && "font-bold"
                              )}
                              key={opt.value}
                              onClick={() => {
                                setActiveFilters((prev) => ({ ...prev, [col.key]: opt.value }));
                                setOpenFilter(null);
                              }}
                            >
                              {opt.label}
                            </div>
                          ))}
                        </>
                      )}
                    </div>
                  </Dropdown>
                </div>
              ))}
            </Card>
          )}
        </div>

        {paginatedData.map((row, i) => (
          <Card key={i} borderColor='gray-300'>
            {columns.map((col, index) => (
              <div key={index} className={clsx(col.hidden && "hidden")}>
                <div className='flex py-2 gap-2'>
                  <div className='w-1/3'>{col.label} :</div>
                  <div>{col.render ? col.render(row[col.key], row) : String(row[col.key])}</div>
                </div>
                {index < columns.length - 1 &&
                  <hr className='my-2 border-gray-400' />
                }
              </div>
            ))}

            {/* Affiche les boutons d'actions */}
            {actions.length > 0 && (
              <CardActions position='end' className='flex-col sm:flex-row'>
                {actions.map((action, j) => {
                  const isHidden = typeof action.hidden === 'function' ? action.hidden(row) : !!action.hidden;
                  return !isHidden && (
                    <Button
                      key={j}
                      variant={action.buttonVariant}
                      icon={action.icon}
                      onClick={() => action.onClick(row)}
                    >
                      {action.label}
                    </Button>
                  );
                })}
              </CardActions>
            )}
          </Card>
        ))}
      </div>

      {/* Pagination */}
      {enablePagination &&
        <div className="flex justify-between items-center pt-2">
          <Button
            disabled={currentPage === 1}
            variant='text'
            icon={<ChevronLeftIcon />}
            onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
          >
            <span className='hidden sm:block'>Précédent</span>
          </Button>
          <span className="text-sm text-muted-foreground">
            Page {currentPage} sur {totalPages}
          </span>
          <Button
            disabled={currentPage === totalPages}
            variant='text'
            iconRight={<ChevronRightIcon />}
            onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
          >
            <span className='hidden sm:block'>Suivant</span>
          </Button>
        </div>
      }
    </>
  );
}
